import type { DeathCause } from "../economy-rules";
import {
  applyManaGain,
  applyTroopDeath,
  clearArmyValueOnMasterDeath,
  masterDropAmount,
  sigilsFromLifetime,
  unitKillReturn,
} from "../economy-rules";
import { FfaEvent } from "../sim-events";
import type { SimEntity, UnitRole, WorldState } from "./types";
import {
  C,
  canAttack,
  dist,
  dropOrbsAt,
  emit,
  recalcSpecialResidual,
  setMessage,
} from "./shared";

export type HitMode = "single" | "cone" | "line" | "circle";

export type HitProfile = {
  mode: HitMode;
  maxTargets: number;
  /** Full cone width in degrees (aim ± half). Used by cone/line. */
  coneDeg: number;
  /** Damage multiplier for non-primary targets. */
  secondaryMul: number;
  /** Ranged: hold distance / kite instead of closing to melee. */
  standoff: boolean;
  /** Fraction of atkRange — stop approaching at or below this. */
  holdMul: number;
  /** Fraction of atkRange — retreat when closer than this. */
  retreatMul: number;
};

const DEFAULT_HIT: HitProfile = {
  mode: "single",
  maxTargets: 1,
  coneDeg: 0,
  secondaryMul: 1,
  standoff: false,
  holdMul: 0.85,
  retreatMul: 0,
};

export function hitProfileFor(role: UnitRole): HitProfile {
  const table = (
    C as { hitProfiles?: Record<string, Partial<HitProfile>> }
  ).hitProfiles;
  const p = table?.[role];
  if (!p) return DEFAULT_HIT;
  return {
    mode: (p.mode as HitMode) ?? "single",
    maxTargets: Math.max(1, Number(p.maxTargets) || 1),
    coneDeg: Math.max(0, Number(p.coneDeg) || 0),
    secondaryMul: Math.max(0, Math.min(1, Number(p.secondaryMul) ?? 1)),
    standoff: Boolean(p.standoff),
    holdMul: Math.max(0.1, Math.min(1, Number(p.holdMul) || 0.85)),
    retreatMul: Math.max(0, Math.min(1, Number(p.retreatMul) || 0)),
  };
}

function angleDiff(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return Math.abs(d);
}

/**
 * Primary is always first (full damage). Extra targets by hit mode.
 */
export function collectHitTargets(
  world: WorldState,
  attacker: SimEntity,
  primary: SimEntity,
  candidates: SimEntity[],
  profile: HitProfile,
): SimEntity[] {
  const max = Math.max(1, profile.maxTargets);
  if (profile.mode === "single" || max <= 1) return [primary];

  const range = attacker.atkRange;
  const aim = Math.atan2(primary.y - attacker.y, primary.x - attacker.x);
  const defaultCone = profile.mode === "line" ? 25 : 80;
  const half =
    profile.mode === "circle"
      ? Math.PI
      : (((profile.coneDeg || defaultCone) * Math.PI) / 180) / 2;

  type Cand = { e: SimEntity; d: number };
  const extras: Cand[] = [];
  for (let i = 0; i < candidates.length; i++) {
    const o = candidates[i]!;
    if (o.id === primary.id) continue;
    if (o.teamId === attacker.teamId) continue;
    if (!canAttack(world, attacker.teamId, o.teamId)) continue;
    const d = dist(attacker.x, attacker.y, o.x, o.y);
    if (d > range) continue;
    const teamT = world.teams[o.teamId];
    if (teamT && world.time < teamT.protectUntil) continue;
    if (profile.mode !== "circle") {
      const ang = Math.atan2(o.y - attacker.y, o.x - attacker.x);
      if (angleDiff(ang, aim) > half) continue;
    }
    extras.push({ e: o, d });
  }
  extras.sort((a, b) => a.d - b.d);

  const out: SimEntity[] = [primary];
  for (let i = 0; i < extras.length && out.length < max; i++) {
    out.push(extras[i]!.e);
  }
  return out;
}

/** Apply hits to primary + splash; returns whether any target died. */
export function resolveAttackHits(
  world: WorldState,
  attacker: SimEntity,
  primary: SimEntity,
  candidates: SimEntity[],
): boolean {
  const profile = hitProfileFor(attacker.role);
  const targets = collectHitTargets(
    world,
    attacker,
    primary,
    candidates,
    profile,
  );
  let killed = false;
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i]!;
    const mul = i === 0 ? 1 : profile.secondaryMul;
    const dmg = attacker.atk * mul;
    if (dmg <= 0) continue;
    const teamT = world.teams[t.teamId];
    if (teamT && world.time < teamT.protectUntil) continue;
    applyHit(world, attacker, t, dmg);
    if (t.hp <= 0) killed = true;
  }
  return killed;
}

export function applyHit(
  world: WorldState,
  attacker: SimEntity,
  target: SimEntity,
  damage: number,
): void {
  target.hp -= damage;
  const profile = hitProfileFor(attacker.role);
  emit(world, {
    kind: FfaEvent.Hit,
    time: world.time,
    attackerId: attacker.id,
    targetId: target.id,
    teamId: attacker.teamId,
    x: target.x,
    y: target.y,
    damage,
    hp: Math.max(0, target.hp),
    maxHp: target.maxHp,
    targetRole: target.role,
    attackerRole: attacker.role,
    mode: profile.mode,
  });
  if (target.hp <= 0) {
    if (target.role === "master")
      killMaster(world, target.teamId, "pvp", attacker.teamId);
    else killEntity(world, target, attacker.teamId);
  }
}

export function killEntity(
  world: WorldState,
  ent: SimEntity,
  killerTeam: number | null,
): void {
  if (ent.role === "master") return;
  const team = world.teams[ent.teamId];
  if (!team) return;

  emit(world, {
    kind: FfaEvent.Death,
    time: world.time,
    entityId: ent.id,
    x: ent.x,
    y: ent.y,
    role: ent.role,
    teamId: ent.teamId,
    isMaster: false,
  });

  if (
    ent.role === "guardian" ||
    ent.role === "soldier" ||
    ent.role === "raider" ||
    ent.role === "chaser"
  ) {
    const ret = unitKillReturn(ent.cost, C.economy.unitDeathReturnRate);
    team.ledger = applyTroopDeath(team.ledger, ent.cost);
    if (ret > 0) {
      dropOrbsAt(world, ent.x, ent.y, ret, 0.2);
    }
    if (killerTeam !== null && killerTeam !== ent.teamId) {
      const kt = world.teams[killerTeam];
      if (kt?.alive) {
        const gain = C.economy.unitKillMana;
        const beforeSig = sigilsFromLifetime(
          kt.ledger.lifetimeEarnedMana,
          C.economy.sigilEveryMana,
        );
        kt.ledger = applyManaGain(kt.ledger, gain);
        const afterSig = sigilsFromLifetime(
          kt.ledger.lifetimeEarnedMana,
          C.economy.sigilEveryMana,
        );
        kt.sigils += afterSig - beforeSig;
      }
    }
  } else {
    recalcSpecialResidual(world, ent.teamId);
  }

  world.entities = world.entities.filter((e) => e.id !== ent.id);
  recalcSpecialResidual(world, ent.teamId);
}

export function killMaster(
  world: WorldState,
  teamId: number,
  cause: DeathCause,
  killerTeam: number | null,
): void {
  const team = world.teams[teamId];
  if (!team?.alive) return;
  const master = world.entities.find((e) => e.id === team.masterId);
  if (!master) return;

  const drop = masterDropAmount(team.ledger, cause, {
    dropMode: world.dropMode,
    pvpRate: C.economy.pvpDeathRate,
    envRate: C.economy.envDeathRate,
    maxCap: C.economy.maxDropCap,
  });
  team.lastDeathDrop = drop;
  dropOrbsAt(world, master.x, master.y, drop, C.economy.dropCollectDelaySec);
  emit(world, {
    kind: FfaEvent.MasterDrop,
    time: world.time,
    x: master.x,
    y: master.y,
    value: drop,
    teamId,
  });
  emit(world, {
    kind: FfaEvent.Death,
    time: world.time,
    entityId: master.id,
    x: master.x,
    y: master.y,
    role: "master",
    teamId,
    isMaster: true,
  });

  for (const e of world.entities) {
    if (e.teamId !== teamId || e.role === "master") continue;
    emit(world, {
      kind: FfaEvent.Death,
      time: world.time,
      entityId: e.id,
      x: e.x,
      y: e.y,
      role: e.role,
      teamId: e.teamId,
      isMaster: false,
    });
  }
  world.entities = world.entities.filter((e) => e.teamId !== teamId);
  team.ledger = clearArmyValueOnMasterDeath(team.ledger);
  team.alive = false;
  team.deadAt = world.time;
  team.summonQueue.length = 0;
  team.summonCd = 0;

  if (killerTeam !== null && killerTeam !== teamId) {
    const killer = world.teams[killerTeam];
    if (killer?.alive) killer.mastersKilled += 1;
  }

  if (team.isPlayer) {
    world.playerPendingReenter = true;
    world.lastRunSummary = {
      surviveSec: world.time - team.runStart,
      peakRank: team.peakRank,
      mastersKilled: team.mastersKilled,
      peakArmy: team.peakArmy,
      lifetimeMana: team.ledger.lifetimeEarnedMana,
      lastDrop: drop,
    };
    setMessage(world, "hud.msgDead", { drop });
  } else {
    setMessage(world, "hud.msgKill", { drop }, team.id);
  }
}
